import React from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { DatePicker, type DateRange } from "@/components/DatePicker";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { Alert } from "@/components/Alert";

interface DatePickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    range: DateRange;
    onRangeChange: (range: DateRange) => void;
    onApply: () => void;
    onClear: () => void;
    minDate?: string;
    maxDate?: string;
}

export const DatePickerModal: React.FC<DatePickerModalProps> = ({
    isOpen,
    onClose,
    range,
    onRangeChange,
    onApply,
    onClear,
    minDate,
    maxDate,
}) => {
    const { t } = useTranslation("global");
    const hasSelection = Boolean(range.start || range.end);

    const handleApply = (): void => {
        onApply();
        onClose();
    };

    const handleClear = (): void => {
        onClear();
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("datepicker.modal_title")}
            maxWidth="lg"
            onSubmit={handleApply}
            submitLabel={t("datepicker.apply")}
            cancelLabel={t("modal.cancel")}
        >
            <div className="py-1">
                <Alert variant="warning" className="mb-4" centered>
                    {t("datepicker.range_hint")}
                </Alert>

                <DatePicker
                    inline
                    mode="range"
                    range={range}
                    onRangeChange={onRangeChange}
                    minDate={minDate}
                    maxDate={maxDate}
                />

                {hasSelection && (
                    <div className="flex justify-center mt-3">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleClear}
                            icon={<Icon value="x" size={13} />}
                        >
                            {t("datepicker.clear")}
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
};
